import { createColumnHelper } from "@tanstack/react-table";
import { format } from "date-fns";

import {
  JourneyListItem,
  JourneyStatusEnum,
} from "@/app/services/journeyService";
import { formatName } from "@/app/utils/formatName";
import masks from "@/app/utils/masks";

import { Badge } from "@/view/components/ui/badge";
import { Button } from "@/view/components/ui/button";
import { useToast } from "@/view/components/ui/use-toast";
import { Copy } from "iconsax-react";
import { twMerge } from "tailwind-merge";

const columnHelper = createColumnHelper<JourneyListItem>();

const StatusBadge = ({ journey }: { journey: JourneyListItem }) => {
  if (journey.status === JourneyStatusEnum.COMPLETED) {
    return (
      <Badge className="border-transparent bg-green-100 text-green-700 hover:bg-green-100">
        Concluída
      </Badge>
    );
  }

  if (journey.stale) {
    return (
      <Badge className="border-transparent bg-red-100 text-red-700 hover:bg-red-100">
        Abandonada
      </Badge>
    );
  }

  return (
    <Badge className="border-transparent bg-amber-100 text-amber-700 hover:bg-amber-100">
      Em andamento
    </Badge>
  );
};

const CpfButton = ({
  name,
  cpf,
  onlyCpf,
}: {
  name?: string;
  cpf: string;
  onlyCpf?: boolean;
}) => {
  const { toast } = useToast();

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(cpf);

      toast({
        title: "CPF copiado!",
      });
    } catch (error) {
      console.error("Erro ao copiar para a área de transferência:", error);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={handleCopy}
      className="flex h-auto w-full flex-col items-start justify-start px-0 font-normal hover:bg-transparent"
    >
      {name && <p className="text-left">{formatName(name)}</p>}
      <div
        className={twMerge(
          "flex items-center justify-start gap-2 text-left text-xs text-gray-400",
          onlyCpf && "text-base text-black01",
        )}
      >
        <span>{masks.cpf(cpf)}</span>
        <Copy size={16} />
      </div>
    </Button>
  );
};

const buildColumns = () => [
  columnHelper.accessor("customer", {
    id: "customer",
    header: () => (
      <div className="flex w-full flex-col items-start gap-1 pl-1">
        <div className="block">Cliente</div>
        <div className="block text-sm text-gray-400 xl:hidden">Vendedor</div>
      </div>
    ),
    cell: ({ row }) => {
      const { customer, cpf, seller } = row.original;

      return (
        <div className="flex flex-col items-start gap-1 pl-2">
          {customer?.name ? (
            <>
              {customer.cpf ? (
                <CpfButton name={customer.name} cpf={customer.cpf} />
              ) : (
                "—"
              )}
            </>
          ) : (
            <>
              {cpf ? (
                <CpfButton cpf={cpf} onlyCpf={true} />
              ) : (
                "Não identificado"
              )}
            </>
          )}

          <span className="block text-sm text-gray-400 xl:hidden">
            {seller?.name ? formatName(seller.name) : "—"}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor("seller", {
    id: "seller",
    header: () => (
      <div className="hidden pl-1 text-left xl:block">Vendedor</div>
    ),
    cell: ({ row }) => (
      <div className="hidden pl-2 text-left xl:block">
        <p>
          {row.original.seller?.name
            ? formatName(row.original.seller.name)
            : "—"}
        </p>
      </div>
    ),
  }),
  columnHelper.accessor("currentStep", {
    id: "currentStep",
    header: () => <div className="text-center">Parou em</div>,
    cell: ({ row }) => {
      const { currentStepLabel, furthestStepOrder, totalSteps } = row.original;

      return (
        <div className="flex flex-col items-center">
          <span className="text-sm">{currentStepLabel}</span>
          <span className="text-xs text-gray-400">
            etapa {furthestStepOrder} de {totalSteps}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor("totalAttempts", {
    id: "totalAttempts",
    header: () => <div className="hidden text-center lg:block">Interações</div>,
    cell: ({ row }) => (
      <div className="hidden text-center lg:block">
        {row.original.totalAttempts}
      </div>
    ),
  }),
  columnHelper.accessor("status", {
    id: "status",
    header: () => <div className="text-center">Situação</div>,
    cell: ({ row }) => (
      <div className="flex justify-center">
        <StatusBadge journey={row.original} />
      </div>
    ),
  }),
  columnHelper.accessor("lastStepAt", {
    id: "lastStepAt",
    header: () => (
      <div className="hidden text-center md:block">Última interação</div>
    ),
    cell: ({ row }) => (
      <div className="hidden text-center md:block">
        {format(new Date(row.original.lastStepAt), "dd/MM/yyyy HH:mm")}
      </div>
    ),
  }),
];

export const TableReportJourneysColumnsDefinition = () => ({
  columns: buildColumns(),
});
